import React from "react";
import { useTranslation } from "react-i18next";
import { MessagePartInputExtended } from "@/pages/BulkMessaging/types";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/Avatar";
import { getMessagePartContent } from "@/pages/BulkMessaging/utils/apiHelpers";
import { useOrg } from "@/hooks/useOrg";
import { Logo } from "@/assets/svg/Logo";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ClickableLinkPlugin } from "@lexical/react/LexicalClickableLinkPlugin";
import { buildEditorConfig } from "../WYSIWYGEditor/utils/config";

interface ReadOnlyMessageBubbleProps {
  messagePart: MessagePartInputExtended;
  senderName?: string;
  senderProfilePhoto?: { url?: string | null } | null;
}

export const ReadOnlyMessageBubble = ({
  messagePart,
  senderName,
  senderProfilePhoto,
}: ReadOnlyMessageBubbleProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.composeMessage",
  });
  const { org } = useOrg();

  const renderContent = () => {
    const content = getMessagePartContent(messagePart);

    // Handle text content
    if (content.text !== undefined && !content.isImageElement) {
      return (
        <LexicalComposer
          initialConfig={buildEditorConfig({
            text: content.text,
            editable: false,
          })}
        >
          <RichTextPlugin
            contentEditable={<ContentEditable className="text-foreground" />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <LinkPlugin />
          <ClickableLinkPlugin newTab={true} />
        </LexicalComposer>
      );
    }

    // Handle attachment content
    if (content.attachment) {
      const imageSrc =
        content.attachment instanceof File
          ? URL.createObjectURL(content.attachment)
          : content.attachment.url;

      return (
        <div className="space-y-3">
          <div className="space-y-2">
            <div className="overflow-hidden rounded-lg">
              <img
                src={imageSrc}
                alt={t("uploadedImageAlt", { number: 1 })}
                className="h-auto w-full object-cover"
              />
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const avatarSrc = senderProfilePhoto?.url || org?.logo?.url;
  const avatarFallback = senderName?.charAt(0) || "U";

  return (
    <div className="flex items-start gap-1.5">
      <div className="flex-shrink-0">
        <Avatar>
          {avatarSrc ? (
            <>
              <AvatarImage src={avatarSrc} alt={t("senderAvatarAlt")} />
              <AvatarFallback>{avatarFallback}</AvatarFallback>
            </>
          ) : (
            <div data-testid="logo-fallback">
              <Logo />
            </div>
          )}
          <AvatarFallback>{avatarFallback}</AvatarFallback>
        </Avatar>
      </div>

      <div className="w-80 rounded-lg">
        <div>
          <span className="text-sm font-medium text-foreground">
            {senderName || t("defaultSenderName")}
          </span>
        </div>

        <div className="mt-2 [&_a]:text-blue-primary [&_a]:underline [&_a:hover]:text-blue-600 [&_a:visited]:text-purple-600">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
